import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApprovalFlowService } from '../../approval-flow-models/approval-flow.service';
import { CommissionReportConfigService } from 'src/app/service/commission-summary/commission-report-config.service';
import { NotificationService } from 'src/app/service/notification.service';
import { NotificationType } from 'src/app/enum/notification-type.enum';
import { ExportExcelService } from 'src/app/service/export-excel.service';
import {
  calculateKpiColumnTotals,
  buildCommissionReportExcelPayload,
  CHARGEBACK_DISPLAY_NOTICE,
  CHARGEBACK_DISPLAY_NOTICE_TITLE,
  CommReportEmployeeRow,
  CommReportKpiConfigByPlan,
  getKpiCellDisplayLines,
  getKpiColumnSubtitleParts,
  getKpiCellDisplay,
  hasVisibleDisplayOnlyKpi,
  isDisplayOnlyKpi,
  KpiCellDisplay,
  KpiColumnConfig,
  setupCommReportDisplay
} from 'src/app/shared/utils/commission-report-pivot.util';

@Component({
  selector: 'app-comm-preview-modal',
  templateUrl: './comm-preview-modal.component.html',
  styleUrls: ['./comm-preview-modal.component.css']
})
export class CommissionReportPreviewModalComponent implements OnInit {

  trackingId: string;
  loading = false;
  pivotedData: CommReportEmployeeRow[] = [];
  filteredData: CommReportEmployeeRow[] = [];
  filterText = '';
  visibleKpiConfigs: KpiColumnConfig[] = [];
  configsByPlan: CommReportKpiConfigByPlan = {};
  grandTotalCommission = 0;
  filteredGrandTotal = 0;
  payrollMonth = '';

  readonly chargebackNotice = CHARGEBACK_DISPLAY_NOTICE;
  readonly chargebackNoticeTitle = CHARGEBACK_DISPLAY_NOTICE_TITLE;

  get showChargebackNotice(): boolean {
    return hasVisibleDisplayOnlyKpi(this.visibleKpiConfigs);
  }

  isDisplayOnlyKpi = isDisplayOnlyKpi;

  constructor(
    public dialogRef: MatDialogRef<CommissionReportPreviewModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private approvalFlowService: ApprovalFlowService,
    private commissionReportConfigService: CommissionReportConfigService,
    private notificationService: NotificationService,
    private exportExcelService: ExportExcelService
  ) {
    this.trackingId = data.trackingId;
  }

  ngOnInit(): void {
    this.loadCommissionReportData();
  }

  loadCommissionReportData() {
    this.loading = true;

    this.approvalFlowService.getCommissionReportPreview(this.trackingId)
      .subscribe(
        (response: any) => {
          if (response?.source === 'snapshot') {
            this.applySnapshot(response);
            return;
          }

          const rawData = Array.isArray(response) ? response : (response?.rawData || []);
          const planIds = this.extractPlanIds(rawData);
          this.commissionReportConfigService.loadKpiConfigs(planIds).subscribe(
            savedConfigs => this.applyReportData(rawData, planIds, savedConfigs),
            () => this.applyReportData(rawData, planIds, {})
          );
        },
        () => {
          this.notificationService.sendNotification(NotificationType.ERROR,
            'Error loading commission report preview');
          this.loading = false;
        }
      );
  }

  exportToExcel() {
    if (!this.pivotedData.length) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'No data available to export');
      return;
    }

    if (!this.visibleKpiConfigs.length) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'No configured KPI columns to export');
      return;
    }

    this.exportExcelService.exportExcel(
      buildCommissionReportExcelPayload(
        this.pivotedData,
        this.visibleKpiConfigs,
        this.payrollMonth,
        this.grandTotalCommission,
        this.configsByPlan
      )
    );
    this.notificationService.sendNotification(NotificationType.SUCCESS, 'Report exported successfully');
  }

  applyFilter() {
    const filterValue = this.filterText.toLowerCase().trim();

    if (!filterValue) {
      this.filteredData = [...this.pivotedData];
    } else {
      this.filteredData = this.pivotedData.filter(emp =>
        emp.empname?.toLowerCase().includes(filterValue) ||
        emp.sales_rep_id?.toLowerCase().includes(filterValue) ||
        emp.employee_id?.toLowerCase().includes(filterValue) ||
        emp.supervisor_name?.toLowerCase().includes(filterValue) ||
        emp.manager_name?.toLowerCase().includes(filterValue) ||
        emp.comm_plan?.toLowerCase().includes(filterValue) ||
        this.visibleKpiConfigs.some(config => {
          const kpiData = emp.kpis[config.kpiName];
          return kpiData && (
            config.kpiName.toLowerCase().includes(filterValue) ||
            kpiData.commission_val?.toString().includes(filterValue) ||
            kpiData.units?.toString().includes(filterValue) ||
            kpiData.goal?.toString().includes(filterValue) ||
            kpiData.percentage?.toString().includes(filterValue) ||
            kpiData.revenue?.toString().includes(filterValue)
          );
        })
      );
    }

    this.filteredGrandTotal = this.filteredData.reduce((sum, emp) => sum + emp.total_commission, 0);
  }

  clearFilter() {
    this.filterText = '';
    this.applyFilter();
  }

  getCellDisplay(emp: CommReportEmployeeRow, config: KpiColumnConfig): KpiCellDisplay {
    return getKpiCellDisplay(this.configsByPlan, emp.comm_plan_id, config.kpiName, config);
  }

  getColumnTotals(config: KpiColumnConfig) {
    return calculateKpiColumnTotals(this.filteredData, [config], this.configsByPlan)[0];
  }

  getKpiSubtitleParts(config: KpiColumnConfig): string[] {
    return getKpiColumnSubtitleParts(config);
  }

  getKpiCellLines(emp: CommReportEmployeeRow, config: KpiColumnConfig) {
    const cell = this.getCellDisplay(emp, config);
    return getKpiCellDisplayLines(emp.kpis[config.kpiName], cell, config.kpiName);
  }

  close(): void {
    this.dialogRef.close();
  }

  /** Use the exact report the admin submitted — do not reload live KPI config. */
  private applySnapshot(snapshot: any) {
    this.pivotedData = Array.isArray(snapshot.pivotedData) ? snapshot.pivotedData : [];
    this.filteredData = [...this.pivotedData];
    this.visibleKpiConfigs = Array.isArray(snapshot.visibleKpiConfigs) ? snapshot.visibleKpiConfigs : [];
    this.configsByPlan = this.normalizeConfigsByPlan(snapshot.configsByPlan);
    this.payrollMonth = snapshot.payrollMonth || '';
    this.grandTotalCommission = Number(snapshot.grandTotalCommission) || 0;
    this.filteredGrandTotal = this.filteredData.reduce((sum, emp) => sum + (emp.total_commission || 0), 0);
    this.loading = false;

    if (!this.pivotedData.length) {
      this.notificationService.sendNotification(NotificationType.ERROR,
        'Submitted report snapshot is empty');
    }
  }

  private applyReportData(response: any[], planIds: number[], savedConfigs: CommReportKpiConfigByPlan) {
    const state = setupCommReportDisplay(response, planIds, savedConfigs);
    this.pivotedData = state.pivotedData;
    this.filteredData = state.filteredData;
    this.configsByPlan = state.configsByPlan;
    this.visibleKpiConfigs = state.visibleKpiConfigs;
    this.payrollMonth = state.payrollMonth;
    this.grandTotalCommission = state.grandTotalCommission;
    this.filteredGrandTotal = state.filteredGrandTotal;
    this.loading = false;
  }

  private extractPlanIds(response: any[]): number[] {
    const ids = new Set<number>();
    (response || []).forEach(row => {
      const planId = Number(row.comm_plan_id);
      if (planId) {
        ids.add(planId);
      }
    });
    return Array.from(ids);
  }

  /** Jackson may serialize numeric map keys as strings. */
  private normalizeConfigsByPlan(configsByPlan: any): CommReportKpiConfigByPlan {
    const normalized: CommReportKpiConfigByPlan = {};
    if (!configsByPlan || typeof configsByPlan !== 'object') {
      return normalized;
    }
    Object.keys(configsByPlan).forEach(key => {
      const planId = Number(key);
      if (planId) {
        normalized[planId] = configsByPlan[key];
      }
    });
    return normalized;
  }
}
